import { Fragment, useState } from "react";
import { useInView } from "react-intersection-observer";
import { RouteFactory } from "@app/routePaths";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import { AdUnitNames } from "@components/Advertisement/utils/types";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import Header from "@components/Header/Header";
import {
  getSectionNavigationPath,
  useArticleHeaderState,
} from "@components/Header/helpers";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import useArticleGiftReceived from "@hooks/useArticleGiftReceived";
import { useDelayedComponents } from "@hooks/useDelayedComponents";
import useGenerateGiftURL from "@hooks/useGenerateGiftURL";
import { useGrapeshotData } from "@hooks/useGrapeshot";
import useIsOutbrainEnabled from "@hooks/useIsOutbrainEnabled";
import { ArticlePageData } from "@pages/Article/ArticleContentAppWebView";
import AppBanner from "@pages/Article/components/AppBanner";
import ArticleDivider from "@pages/Article/components/ArticleDivider";
import ArticleSubshareModal from "@pages/Article/components/ArticleSubshare/ArticleSubshareModal";
import ArticleSubsharePrompt from "@pages/Article/components/ArticleSubshare/ArticleSubsharePrompt";
import ArticleSubshareReceived from "@pages/Article/components/ArticleSubshare/ArticleSubshareReceived";
import LayoutFactory from "@pages/Article/components/Layout/LayoutFactory";
import { useNextArticle } from "@pages/Article/hooks/useNextArticle";
import { LAMBDA_RESPONSE_TYPES } from "@pages/Article/types/Alacrity";
import { VariantType } from "@pages/Article/utils/enums";
import {
  getUrlPathFromAbsoluteUrl,
  VerticalHeaderFactory,
} from "@pages/Article/utils/helpers";
import { ArticlePageContext } from "@pages/Article/utils/types";
import { FeedTag } from "@pages/Keyword/utils/types";
import { getFormattedTextForAds } from "@pages/Section/helpers";
import useOKTAUserStore from "@store/useOKTAUserStore";
import { ArticleDisplayType } from "@typings/enums";
import { useFeatureFlagVariantKey } from "posthog-js/react";

export type ArticleContentProps = Pick<ArticlePageContext, "data">;

const ArticlePageContent = ({
  data,
}: ArticleContentProps): React.ReactElement => {
  const [showGiftSubscribePrompt, setShowGiftSubscribePrompt] = useState(false);
  const [articleInView, setArticleInView] = useState<ArticlePageData>(data);

  const { isModalOpen, setIsModalOpen, alacrityRes, isGiftReceived } =
    useArticleGiftReceived({ paidMode: data.paidMode });

  const isOutbrainEnabled = useIsOutbrainEnabled();
  const { isShown } = useDelayedComponents(800);

  const { articleTopRef, isShowArticleHeader } = useArticleHeaderState();

  // Article data de-construct
  const { sections, seoFooterData, tags, paidMode, isChinese, sentiment } =
    data;

  // The main section of the article.
  const mainSection = articleInView.sections?.[0] || sections?.[0];
  const mainSectionURLPath = getUrlPathFromAbsoluteUrl(mainSection.urlPath);

  // The parent section if main section has parent, otherwise this is also the main section.
  const parentSection = mainSection?.parent || mainSection;
  const parentSectionUrlPath =
    getUrlPathFromAbsoluteUrl(parentSection?.urlPath) || mainSectionURLPath;

  const verticalPath = mainSectionURLPath || "";
  const isThisArticlegifted =
    alacrityRes?.data.responseType ===
      LAMBDA_RESPONSE_TYPES.SUCCESS_VALID_GIFT_TOKEN &&
    !alacrityRes.data.viewGiftStatus;

  const VerticalHeaderLayout = VerticalHeaderFactory({ verticalPath });

  const { ref: showGiftSubscribePromptRef } = useInView({
    triggerOnce: true,
    onChange: (inView) => {
      if (inView && isThisArticlegifted) setShowGiftSubscribePrompt(true);
    },
  });

  const handleSetArticle = (e: ArticlePageData) => {
    setArticleInView(e);
  };

  const sectionPath = getSectionNavigationPath(
    tags || [],
    parentSectionUrlPath
  );

  const disablePrestitialBasedOnUrlPath = [
    RouteFactory.paidPressRelease, // paid press release pages
    RouteFactory.section("events-awards"), // events-awards pages
  ].some((path) => data?.urlPath.startsWith(path));

  const disablePrestitialBasedOnTags = [RouteFactory.keywordsBudget2025].some(
    (keyword) => tags?.some((tag) => tag.urlPath === keyword)
  );

  const adsNames: AdUnitNames[] =
    disablePrestitialBasedOnUrlPath || disablePrestitialBasedOnTags
      ? ["lb1", "imu1", "imu2", "imu3", "midarticlespecial", "catfish", "abm"]
      : [
          "lb1",
          "imu1",
          "imu2",
          "imu3",
          "midarticlespecial",
          "prestitial",
          "catfish",
          "abm",
        ];

  const isPremium = paidMode === "premium";

  const {
    alacrityRes: giftURlAlacrityRes,
    isModalOpen: giftURlisModalOpen,
    setIsModalOpen: setGiftURLIsModalOpen,
    handleGenerateGiftURLModal: handleGenerateGiftURLModal,
  } = useGenerateGiftURL(isPremium);

  const articleInViewGiftRes = giftURlAlacrityRes.find(
    (res) => res.pathname === getUrlPathFromAbsoluteUrl(articleInView.urlPath)
  );

  const OKTAUserInfo = useOKTAUserStore((state) => state.userInfo);

  const { moreStories, isLoading, ref } = useNextArticle({
    data,
    lang: isChinese ? "zh-hans" : "en",
  });

  const firstArticleUrlPath =
    getUrlPathFromAbsoluteUrl(moreStories[0]?.urlPath) || "";
  const { gsChannels } = useGrapeshotData({
    urlPath: firstArticleUrlPath,
    index: -1,
    keywords: sentiment?.keywords,
  });

  const removeMoreVariant = useFeatureFlagVariantKey(
    "feature_remove_more_sections"
  )?.toString();

  // eslint-disable-next-line no-console
  console.log("[par]", { moreStories });

  return (
    <div className="w-full" data-testid="article-content-component">
      <AdSettings
        adNames={adsNames}
        path={mainSectionURLPath || "/"}
        pageType="articles"
        adTargets={[
          { key: "page", value: "article" },
          { key: "abVariant", value: removeMoreVariant || VariantType.CONTROL },
        ]}
        slotTargets={[
          { key: "btarticleid", value: data?.id },
          {
            key: "bttags",
            value:
              data?.tags
                ?.map((tag: FeedTag) => getFormattedTextForAds(tag.name))
                .join(",") || "",
          },
          { key: "gs_channels", value: gsChannels },
        ]}
      />

      <Ad
        adType="lb1"
        className="no-print flex min-h-[50px] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 md:py-3 lg:min-h-[250px]"
      />

      <Header
        article={articleInView}
        displayArticle={isShowArticleHeader}
        parentCategory={sectionPath}
        handleGenerateGiftURLModal={handleGenerateGiftURLModal}
        setIsModalOpen={setGiftURLIsModalOpen}
        alacrityRes={articleInViewGiftRes?.alacrityRes}
        isPremium={isPremium}
        userType={OKTAUserInfo?.usertype}
      />

      <PulseEntry />

      {VerticalHeaderLayout ? (
        <Container rootClassName="py-2">
          <VerticalHeaderLayout />
        </Container>
      ) : null}

      <div
        id="article-content-wrapper"
        className="relative w-full text-gray-850"
      >
        {moreStories.map((article, index) => {
          // Check if article is undefined
          if (!article) return null;

          const displayType =
            article?.displayType || ArticleDisplayType.MainNewsDefault;

          const ArticleLayout = LayoutFactory({
            displayType,
            path: getUrlPathFromAbsoluteUrl(article.urlPath),
          });

          if (!ArticleLayout) return null;

          return (
            <Fragment key={article.id}>
              {index > 0 ? <ArticleDivider /> : null}

              <ArticleLayout
                article={article}
                index={index}
                gsChannels={gsChannels}
                handleGenerateGiftURLModal={handleGenerateGiftURLModal}
                setIsModalOpen={setGiftURLIsModalOpen}
                alacrityRes={articleInViewGiftRes?.alacrityRes}
                isPremium={isPremium}
                isModalOpen={giftURlisModalOpen}
                setArticleObj={handleSetArticle}
                articleInView={articleInView}
                displayArticleRef={articleTopRef}
                isGifted={isGiftReceived}
                isPreview={data.isPreview}
                readMoreSectionOrTagData={article.readMoreSectionOrTagData}
                isOutbrainEnabled={isOutbrainEnabled}
                isNavigate={moreStories.length > 1}
                isChinese={isChinese}
                isSummaryAvailable={article.isSummaryAvailable}
              />

              <div ref={ref} />
            </Fragment>
          );
        })}
      </div>

      {isLoading ? <ArticleDivider isLoading={isLoading} /> : null}

      <PulseEntry />

      <ArticleSubsharePrompt
        isModalOpen={showGiftSubscribePrompt}
        setIsModalOpen={setShowGiftSubscribePrompt}
      />

      <div
        aria-roledescription="article-subshare-prompt-observer"
        ref={showGiftSubscribePromptRef}
      ></div>

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />

      <Ad adType="abm" />

      <Ad adType="prestitial" />

      <>
        {alacrityRes ? (
          <ArticleSubshareModal
            isModalOpen={isModalOpen && !alacrityRes.data.viewGiftStatus}
            setIsModalOpen={setIsModalOpen}
          >
            <ArticleSubshareReceived
              alacrityRes={alacrityRes}
              setIsModalOpen={setIsModalOpen}
            />
          </ArticleSubshareModal>
        ) : null}
      </>

      {isShown ? <AppBanner /> : null}
    </div>
  );
};

export default ArticlePageContent;
